import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  zIndex,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import { ConfirmDialog } from './confirm-dialog';
import { ModalPortal } from './modal-portal';
import { Presence, m, scrimMotion, useOverlayMotion } from '../../motion';

/**
 * THE SHARED FORM MODAL — header, one scroller, pinned footer.
 *
 * The building modal (dashboard-buildings.styled.tsx) worked out this shape and
 * paid for it: an auto-height card lets the inner `height: 100%` scroller
 * resolve to auto, so the scroller never activates, the card grows past the
 * viewport and the SCRIM scrolls instead — the header and the save button both
 * drift off screen. An explicit card height is what makes "fixed header,
 * scrolling body, footer at the bottom" true.
 *
 * This is that shape, extracted, so the next screen that needs a form in a
 * layer does not re-derive it. Adding a user and adding a resident were full
 * PAGES until 2026-08-11; both are one form raised from a list, which is what a
 * modal is for — the list stays behind it, and closing goes back to it rather
 * than re-navigating.
 *
 * ═══════════ MOUNTED FROM A ROUTE, NOT FROM A BOOLEAN ═══════════
 * Both callers are addressed by URL ('/dashboard/users/add-new-user'), so this
 * component takes no `open` prop: it is open because it is mounted, and
 * `onClose` navigates. That keeps deep links, refreshes and the browser's back
 * button all meaning the same thing.
 *
 * ═══════════ …EXCEPT WHERE A CALLER WANTS THE EXIT ═══════════
 * A modal that is unmounted the instant it closes cannot animate out — the
 * node is gone before a frame is drawn. Callers that gate on a boolean
 * (`{open && <FormModal/>}`) may instead pass `open`: the conditional then
 * lives INSIDE the Presence below, so closing plays the exit before the portal
 * unmounts. Left undefined, nothing changes: mounted means open, the entrance
 * still plays, and unmounting is instant as before.
 *
 * Portaled — see ./modal-portal.tsx: the dashboard's entrance animation creates
 * a transform containing block, and a fixed scrim inside one covers its pane
 * rather than the viewport.
 */

const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Card = m.div<{ $wide?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  max-width: ${p => (p.$wide ? '720px' : '560px')};
  /* Explicit, for the reason in the header. The dvh twin is the iOS rule from
     the responsive pass: 92vh there is the tall-toolbar value, so the footer
     sits under the browser chrome. */
  height: min(92vh, 780px);
  height: min(92dvh, 780px);
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.lg};

  ${down('sm')} {
    max-width: none;
    height: calc(100vh - ${spacing.sm});
    height: calc(100dvh - ${spacing.sm});
    border-radius: ${radius.lg} ${radius.lg} 0 0;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.sm};
  flex: none;
  padding: ${spacing.md} ${spacing.md} ${spacing.sm};
  border-bottom: 1px solid ${palette.line};
`;

const Heading = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: -0.014em;
  color: ${palette.ink};
`;

const Subtitle = styled.p`
  margin: ${spacing.half} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* A 44px target, not a 24px glyph. This is the control somebody presses to
   abandon a half-filled form, often on a tablet at a desk. */
const Close = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  /* Chrome's default button padding is 1px 6px and shows through wherever a
     button sets none. The glyph is centred in a fixed 44px circle, so there is
     nothing for padding to do here. */
  padding: 0;
  margin: -${spacing.half} -${spacing.half} 0 0;
  border: 1px solid transparent;
  border-radius: ${radius.pill};
  background: transparent;
  color: ${palette.slate};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.line};
    color: ${palette.ink};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: ${spacing.md};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.sm};
  flex: none;
  flex-wrap: wrap;
  padding: ${spacing.sm} ${spacing.md};
  /* The safe area, so the save is not under a home indicator on the phone
     sheet. The variable is zero at a mouse, so a desktop card is unchanged. */
  padding-bottom: calc(${spacing.sm} + var(--safe-bottom));
  border-top: 1px solid ${palette.line};
  background: ${palette.surface};
`;

export interface FormModalProps {
  title: string;
  subtitle?: React.ReactNode;
  /** Rendered in the pinned footer. Left out, the footer is not drawn at all. */
  footer?: React.ReactNode;
  /** 720px instead of 560px, for forms with a second column or a tab strip. */
  wide?: boolean;
  /**
   * OPTIONAL, for the exit animation. Undefined means "open because mounted"
   * (the route-addressed callers). A boolean keeps the component mounted and
   * lets closing animate out — see the header.
   */
  open?: boolean;
  onClose: () => void;
  /**
   * TRUE WHEN THERE IS TYPED WORK IN HERE THAT WOULD BE LOST.
   *
   * Escape, a scrim mousedown and the × all discarded immediately and
   * unconditionally, with no dirty check anywhere. The building edit modal is
   * the worst case in the product: a name, a map location picked by hand, a
   * timezone, contact details and a portfolio move, all gone to one stray key.
   *
   * Left undefined, nothing changes — a modal with nothing to lose should not
   * ask. Passing it turns the three dismissals into one confirmation.
   */
  isDirty?: boolean;
  /**
   * TRUE WHILE THE FORM'S SAVE IS ON ITS WAY (opt-in; resident-portal RP-9.8).
   *
   * The request has left and its answer has not come back. Closing now neither
   * cancels it nor shows what it did: the editor went away, the pass was
   * created all the same, and a reader who thought they had backed out added
   * it a second time. While this is true the ×, Escape and the scrim do
   * nothing — the Save button already says "Saving…" — and they work again
   * the moment it settles.
   *
   * EXPLICIT, NEVER INFERRED. Only a caller that passes it gets the guard; a
   * form that leaves it undefined closes exactly as it always has.
   */
  saving?: boolean;
  /** Overridable for a form where "discard" is the wrong word. */
  discardMessage?: string;
  children: React.ReactNode;
}

export const FormModal: React.FC<FormModalProps> = ({
  title,
  subtitle,
  footer,
  wide,
  open,
  onClose,
  isDirty = false,
  saving = false,
  discardMessage = 'You have unsaved changes here. Close anyway and lose them?',
  children,
}) => {
  const isOpen = open ?? true;
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  /* ONE GATE FOR ALL THREE EXITS — Escape, the scrim and the ×. Guarding only
     the × would leave the two accidental routes wide open, and those are the
     ones that lose work. */
  const requestClose = useCallback(() => {
    // A save in flight: wait for its answer (see `saving`).
    if (saving) return;
    if (isDirty) {
      setConfirmingDiscard(true);

      return;
    }

    onClose();
  }, [isDirty, onClose, saving]);

  // A discard prompt cannot outlive the form it guards: if the caller closes
  // the modal by other means while it is up, it goes with the modal.
  useEffect(() => {
    if (!isOpen) setConfirmingDiscard(false);
  }, [isOpen]);

  // Escape and the focus trap come from the shared dialog stack, so a
  // confirmation raised from inside this form takes the key, not this layer.
  const dialogRef = useDialogFocus(isOpen, requestClose);
  const cardMotion = useOverlayMotion('modal');

  return (
    <>
      <Presence>
        {isOpen ? (
          <ModalPortal key="form-modal">
            {/* onMouseDown, not onClick: a click whose press started INSIDE the
                card and finished on the scrim (a text selection dragged out of
                an input) would otherwise close a form somebody was editing. */}
            <Scrim
              {...scrimMotion}
              onMouseDown={event => {
                if (event.target === event.currentTarget) requestClose();
              }}
            >
              <Card
                {...cardMotion}
                ref={dialogRef}
                $wide={wide}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onMouseDown={event => event.stopPropagation()}
              >
                <Head>
                  <Heading>
                    <Title>{title}</Title>
                    {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
                  </Heading>
                  <Close
                    type="button"
                    aria-label="Close"
                    aria-disabled={saving || undefined}
                    onClick={requestClose}
                  >
                    ×
                  </Close>
                </Head>
                <Body>{children}</Body>
                {footer ? <Foot>{footer}</Foot> : null}
              </Card>
            </Scrim>
          </ModalPortal>
        ) : null}
      </Presence>
      {/* Portals itself, and carries its own Presence, so it sits outside the
          form's — it must not be frozen into the form's exit frame. */}
      <ConfirmDialog
        open={confirmingDiscard}
        title="Discard your changes?"
        message={discardMessage}
        primaryLabel="Discard changes"
        primaryVariant="destructive"
        dismissLabel="Keep editing"
        onPrimary={() => {
          setConfirmingDiscard(false);
          onClose();
        }}
        onDismiss={() => setConfirmingDiscard(false)}
      />
    </>
  );
};
